import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { CircularProgress, FormControl, Grid, MenuItem, Select, Stack, Switch, TextField, Typography } from '@mui/material';
import { Controller } from 'react-hook-form';
import { REQUIRED_RED } from '../../App';
import { preventPhoneInputKeys } from '../../components/FormatNumber/FormatNumber';
import { MuiSelectWithFocus, MuiTextFieldWithFocus } from '../../config/MaterialUIConstants';
import { inputFormatPhoneNumber, removeDashFromPhoneNumber } from '../../container/Common/FormatPhoneNumber';
import { PhoneTypeIcon } from '../../container/Customers/Profile/CustomerProfileLeftDataSection';
import { PHONE_TYPES } from '../../utility/Constant';
import ApexAutoSvg from '../../utility/Svg/ApexAutoSvg';
import { useEffect } from 'react';
import { toast } from 'sonner';

const styles = {
    label: {
        fontWeight: 500,
        fontSize: '16px',
        lineHeight: '24px',
        color: '#212121',
    },
    phoneTypeSelect: {
        ...MuiSelectWithFocus,
        '& .MuiSelect-icon': {
            right: '8px !important',
        },
        '& .MuiSelect-select': {
            width: '70px',
            paddingLeft: '12px!important',
            paddingRight: '12px!important',
        },
    },
    phoneTypeMenuProps: {
        PaperProps: {
            style: {
                width: '140px',
                marginTop: '2px',
                marginLeft: '34px',
                boxShadow: '0 3px 14px 2px rgba(0, 0, 0, 0.12), 0 8px 10px 1px rgba(0, 0, 0, 0.14), 0 5px 5px -3px rgba(0, 0, 0, 0.20)',
                // border: '1px solid rgba(0, 0, 0, 0.10)',
                borderRadius: '4px',
            },
        },
    },
    countrySelect: {
        width: 'auto',
        minWidth: 'auto',
        height: '50px',
        border: 'none',
        marginRight: '8px',
        boxShadow: 'none',
    },
    countrySelectMenuProps: {
        PaperProps: {
            style: {
                width: '264px',
                marginLeft: '86px',
                boxShadow: '0px 3px 14px 2px rgba(0, 0, 0, 0.12), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 5px 5px -3px rgba(0, 0, 0, 0.20)',
            },
        },
    },
    countrySelectSx: {
        '& .MuiOutlinedInput-notchedOutline': {
            border: 'none',
        },
        '& .MuiSelect-select': {
            paddingLeft: '0px',
            paddingRight: '32px !important',
            marginRight: '0!important',
            display: 'flex',
            alignItems: 'center',
            gap: '0',
        },
        background: 'transparent',
    },
    flagContainer: {
        width: '22px',
        height: '16px',
        // borderRadius: '50%',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#fff',
        fontSize: '20px',
    },
    welcomeText: {
        fontSize: '14px',
        lineHeight: '120%',
        letterSpacing: '0.15px',
        fontStyle: 'italic',
    },
};

const PhoneField = ({
    label,
    isRequired,
    phoneTypeFieldName,
    phoneNumberFieldName,
    control,
    errors,
    setValue,
    setError,
    clearErrors,
    selectedPhoneTypes,
    setSelectedPhoneTypes,
    phoneTypeKey,
    selectedCountry,
    handleCountryChange,
    countryList,
    phoneInputRef,
    phoneTypes,
    handlePhoneNumberCheck,
    isCheckingPhone,
    setShowCustomerFoundMessage,
}) => {
    return (
        <Stack spacing={1}>
            <Typography variant="body1" style={styles.label}>
                {label} {isRequired && <span style={{ color: REQUIRED_RED }}>*</span>}
            </Typography>

            <Stack direction="row" spacing={1}>
                {/* Phone Type Dropdown */}
                <FormControl>
                    <Controller
                        name={phoneTypeFieldName}
                        control={control}
                        render={({ field }) => (
                            <Select
                                value={selectedPhoneTypes?.[phoneTypeKey] || 'mobile'}
                                onChange={(e) => {
                                    setSelectedPhoneTypes({
                                        ...selectedPhoneTypes,
                                        [phoneTypeKey]: e.target.value,
                                    });
                                    field.onChange(e.target.value);
                                }}
                                MenuProps={styles.phoneTypeMenuProps}
                                sx={styles.phoneTypeSelect}
                                renderValue={(selected) => {
                                    const selectedType = phoneTypes.find((type) => type.key === selected);
                                    return (
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'flex-start',
                                                padding: '3px 7px',
                                                width: '38px',
                                                height: '38px',
                                            }}
                                        >
                                            {PhoneTypeIcon(selectedType.key)}
                                        </div>
                                    );
                                }}
                            >
                                {phoneTypes.map((type) => (
                                    <MenuItem
                                        key={type.key}
                                        value={type.key}
                                        sx={{
                                            gap: 0.5,
                                            paddingTop: '0',
                                            paddingBottom: '0',
                                            paddingLeft: '8px',
                                            paddingRight: '8px',
                                            marginBottom: '0',
                                            fontWeight: 400,
                                            color: '#212121',
                                            lineHeight: '130%',
                                        }}
                                    >
                                        <div style={{ width: '38px', height: '38px', alignItems: 'center', justifyContent: 'center', display: 'flex' }}>
                                            {PhoneTypeIcon(type.key)}
                                        </div>{' '}
                                        {type.value}
                                    </MenuItem>
                                ))}
                            </Select>
                        )}
                    />
                </FormControl>

                {/* Phone Number Input */}
                <Controller
                    name={phoneNumberFieldName}
                    control={control}
                    rules={
                        isRequired
                            ? {
                                  required: 'Phone Number is required',
                                  pattern: {
                                      value: /^\d{3}-\d{3}-\d{4}$/,
                                      message: 'Phone number should be 10 digits long',
                                  },
                              }
                            : {
                                  pattern: {
                                      value: /^\d{3}-\d{3}-\d{4}$/,
                                      message: 'Phone number should be 10 digits long',
                                  },
                              }
                    }
                    render={({ field }) => (
                        <TextField
                            InputProps={{
                                startAdornment: (
                                    <Select
                                        style={styles.countrySelect}
                                        MenuProps={styles.countrySelectMenuProps}
                                        sx={styles.countrySelectSx}
                                        value={selectedCountry?._id ?? ''}
                                        onChange={handleCountryChange}
                                        renderValue={(selected) => {
                                            const country = countryList.find((item) => item._id === selected) || selectedCountry;
                                            if (!country) return 'Select';
                                            return (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <div style={styles.flagContainer}>{country.flag}</div>
                                                    <span style={{ fontSize: '16px', fontWeight: 400 }}>{country.dial_code}</span>
                                                </div>
                                            );
                                        }}
                                        IconComponent={UnfoldMoreIcon}
                                    >
                                        {countryList?.length > 0 &&
                                            countryList.map((country) => (
                                                <MenuItem key={country._id} value={country._id} style={{ height: '44px' }}>
                                                    <span style={{ marginRight: '8px' }}>
                                                        <img src={country?.flag_svg} alt="flag" style={{ width: '24px', height: '12px' }} />
                                                    </span>
                                                    {country.country} ({country.dial_code})
                                                </MenuItem>
                                            ))}
                                    </Select>
                                ),
                                endAdornment: isCheckingPhone && phoneNumberFieldName === 'phoneNumber' && (
                                    <div style={{ width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <CircularProgress size={20} />
                                    </div>
                                ),
                            }}
                            inputRef={phoneInputRef ? (el) => (phoneInputRef.current = el) : null}
                            sx={{
                                ...MuiTextFieldWithFocus,
                                '& .MuiInputBase-root': {
                                    marginRight: '0!important',
                                    borderRadius: '8px',
                                    boxShadow: '0px 2px 3px 0px rgba(0, 0, 0, 0.06)',
                                    height: '50px',
                                },
                            }}
                            variant="outlined"
                            fullWidth
                            error={!!errors[phoneNumberFieldName]}
                            helperText={errors[phoneNumberFieldName] ? errors[phoneNumberFieldName].message : ''}
                            {...field}
                            onChange={(e) => {
                                if (removeDashFromPhoneNumber(e.target.value).length <= 10) {
                                    const input = e.target;
                                    const rawValue = e.target.value;
                                    const prevCursorPosition = input.selectionStart;
                                    const isDeleting = e.nativeEvent.inputType === 'deleteContentBackward';
                                    const hyphenCountBefore = (rawValue.match(/-/g) || []).length;
                                    const formattedValue = inputFormatPhoneNumber(rawValue);
                                    const hyphenCountAfter = (formattedValue.match(/-/g) || []).length;

                                    let newCursorPosition = prevCursorPosition;

                                    if (isDeleting) {
                                        if (hyphenCountAfter < hyphenCountBefore) {
                                            newCursorPosition--;
                                        }
                                    } else {
                                        if (hyphenCountAfter > hyphenCountBefore) {
                                            newCursorPosition++;
                                        }
                                    }

                                    newCursorPosition = Math.max(0, Math.min(newCursorPosition, formattedValue.length));
                                    setValue(phoneNumberFieldName, formattedValue);

                                    setTimeout(() => {
                                        input.setSelectionRange(newCursorPosition, newCursorPosition);
                                    }, 0);

                                    const cleanLength = removeDashFromPhoneNumber(e.target.value).length;
                                    if (cleanLength < 10 && (isRequired || cleanLength > 0)) {
                                        if (phoneNumberFieldName === 'phoneNumber') {
                                            setShowCustomerFoundMessage(false);
                                        }
                                        setError(phoneNumberFieldName, {
                                            message: 'Phone Number should be 10 digits',
                                        });
                                    } else {
                                        clearErrors(phoneNumberFieldName);
                                    }
                                }
                            }}
                            onKeyDown={(e) => {
                                preventPhoneInputKeys(e);
                            }}
                        />
                    )}
                />
            </Stack>
        </Stack>
    );
};

const CustomTextField = ({ label, isRequired, inputRef, name, control, errors, rules, clearErrors, ...rest }) => {
    return (
        <Stack spacing={1}>
            <Typography variant="body1" style={styles.label}>
                {label} {isRequired && <span style={{ color: REQUIRED_RED }}>*</span>}
            </Typography>

            <Controller
                name={name}
                control={control}
                rules={rules}
                render={({ field }) => (
                    <TextField
                        {...field}
                        {...rest}
                        fullWidth
                        variant="outlined"
                        sx={MuiTextFieldWithFocus}
                        error={!!errors[name]}
                        helperText={errors[name] ? errors[name].message : ''}
                        {...field}
                        inputRef={inputRef}
                        value={field.value || ''}
                        onChange={(e) => {
                            let value = e.target.value;

                            if (name === 'firstName' || name === 'lastName') {
                                value = value.replace(/\b\w/g, (char) => char.toUpperCase());
                            }

                            field.onChange(value);

                            if (errors[name]) {
                                clearErrors(name);
                            }
                        }}
                    />
                )}
            />
        </Stack>
    );
};
const CustomerDetailsForm = ({
    control,
    errors,
    setValue,
    phoneInputRef,
    emailRef,
    selectedCountry,
    selectedCountrySecondary,
    countryList,
    handleCountryChange,
    handleCountryChangeSecondary,
    showSendWelcomeEmailToCustomer,
    sendWelcomeEmailToCustomer,
    setSendWelcomeEmailToCustomer,
    setError,
    clearErrors,
    selectedPhoneTypes,
    setSelectedPhoneTypes,
    handlePhoneNumberCheck,
    isCheckingPhone,
    showCustomerFoundMessage,
    setShowCustomerFoundMessage,
}) => {
    const phoneTypes = PHONE_TYPES;

    return (
        <Grid item container xs={12} spacing={1}>
            <Grid item container xs={12} spacing={3}>
                {/* Row 1: Primary Phone and Secondary Phone */}
                <Grid item xs={12} md={6}>
                    <PhoneField
                        label="Primary Phone"
                        isRequired={true}
                        phoneTypeFieldName="primaryPhoneNumberType"
                        phoneNumberFieldName="phoneNumber"
                        phoneTypeKey="primary"
                        selectedCountry={selectedCountry}
                        handleCountryChange={handleCountryChange}
                        countryList={countryList}
                        phoneInputRef={phoneInputRef}
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        setError={setError}
                        clearErrors={clearErrors}
                        selectedPhoneTypes={selectedPhoneTypes}
                        setSelectedPhoneTypes={setSelectedPhoneTypes}
                        phoneTypes={phoneTypes}
                        handlePhoneNumberCheck={handlePhoneNumberCheck}
                        isCheckingPhone={isCheckingPhone}
                        setShowCustomerFoundMessage={setShowCustomerFoundMessage}
                    />
                    {showCustomerFoundMessage && (
                        <Typography
                            variant="caption"
                            sx={{
                                color: '#d32f2f',
                                fontStyle: 'italic',
                                fontSize: '0.875rem',
                                marginTop: '4px',
                                display: 'block',
                            }}
                        >
                            Details auto-filled from Apex. Review and save to add
                        </Typography>
                    )}
                </Grid>

                <Grid item xs={12} md={6}>
                    <PhoneField
                        label="Secondary Phone"
                        isRequired={false}
                        phoneTypeFieldName="secondaryPhoneNumberType"
                        phoneNumberFieldName="secondaryPhoneNumber"
                        phoneTypeKey="secondary"
                        selectedCountry={selectedCountrySecondary}
                        handleCountryChange={handleCountryChangeSecondary}
                        countryList={countryList}
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        setError={setError}
                        clearErrors={clearErrors}
                        selectedPhoneTypes={selectedPhoneTypes}
                        setSelectedPhoneTypes={setSelectedPhoneTypes}
                        phoneTypes={phoneTypes}
                    />
                </Grid>

                {/* Row 2: First Name and Last Name */}
                <Grid item xs={12} md={6}>
                    <CustomTextField
                        label="First Name"
                        isRequired={true}
                        name="firstName"
                        control={control}
                        errors={errors}
                        rules={{ required: 'First Name is required' }}
                        clearErrors={clearErrors}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <CustomTextField
                        label="Last Name"
                        isRequired={false}
                        name="lastName"
                        control={control}
                        errors={errors}
                        // rules={{ required: 'Last Name is required' }}
                        clearErrors={clearErrors}
                    />
                </Grid>

                {/* Row 3: Primary Email and Secondary Email */}
                <Grid item xs={12} md={6}>
                    <CustomTextField
                        id="outlined-basic"
                        label="Primary Email"
                        isRequired={false}
                        name="email"
                        inputRef={emailRef}
                        control={control}
                        errors={errors}
                        rules={{
                            pattern: {
                                value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                                message: 'Please enter valid email address',
                            },
                        }}
                        clearErrors={clearErrors}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <CustomTextField
                        label="Secondary Email"
                        isRequired={false}
                        name="secondaryEmail"
                        control={control}
                        errors={errors}
                        rules={{
                            pattern: {
                                value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                                message: 'Please enter valid email address',
                            },
                        }}
                        clearErrors={clearErrors}
                    />
                </Grid>
            </Grid>
            <Grid item xs={12}>
                {/* Row 4: Send Welcome Email Toggle */}
                {showSendWelcomeEmailToCustomer && (
                    <Grid item xs={12}>
                        <Stack flexDirection={'row'} gap={1} alignItems={'center'}>
                            <Switch
                                checked={sendWelcomeEmailToCustomer}
                                onChange={() => {
                                    setSendWelcomeEmailToCustomer(!sendWelcomeEmailToCustomer);
                                }}
                                color="primary"
                                size="small"
                            />
                            <Stack flexDirection={'row'} width={'100%'} alignItems={'center'}>
                                <Typography sx={{ ...styles.welcomeText, marginRight: '2px' }}>Send Welcome to</Typography> <ApexAutoSvg />
                                <Typography sx={{ ...styles.welcomeText, marginLeft: '0.7px' }}>Apex Auto App email</Typography>
                            </Stack>
                        </Stack>
                    </Grid>
                )}
            </Grid>
        </Grid>
    );
};

export default CustomerDetailsForm;
